/**
 * JoinOnStructuredEditor — the table+column structured editor for a JOIN ON
 * predicate. Each side is a table <select> + column <select> pair, constrained
 * to not overflow the compact popover. Emits `left = right` via `onApply` when
 * the user clicks Apply or submits the form: a side left as parsed keeps the
 * text the user wrote, a re-pointed one is written dialect-exact
 * (`formatOnSide`: the table's qualifier as the query spells it + the
 * column's catalog spelling, delimited when it has no bare form).
 */

import { useState } from 'react';

import { Button } from '../../../../../components/ui/button';
import type { SqlEngine } from '../../../../sql-grammar/model';
import type { InScopeTable } from './inScopeTables';
import { formatOnSide, type OnSide } from './joinOnParse';

/** A parsed ON side: the table + column it resolved to and the text it was written as. */
export type ParsedOnSide = OnSide & { readonly text: string };

export interface StructuredOnEditorProps {
  readonly testId: string;
  readonly tables: readonly InScopeTable[];
  readonly structured: { readonly left: ParsedOnSide; readonly right: ParsedOnSide };
  readonly engine: SqlEngine;
  readonly onCancel: () => void;
  readonly onEditAsText: () => void;
  readonly onApply: (next: string) => void;
}

export function StructuredOnEditor({
  testId,
  tables,
  structured,
  engine,
  onCancel,
  onEditAsText,
  onApply,
}: StructuredOnEditorProps) {
  // Each side's table is held by its `key` (unique — two tables the engine
  // tells apart, e.g. PostgreSQL `"a"` / `"A"`, never share a value).
  const [leftKey, setLeftKey] = useState(structured.left.table.key);
  const [leftCol, setLeftCol] = useState(structured.left.column);
  const [rightKey, setRightKey] = useState(structured.right.table.key);
  const [rightCol, setRightCol] = useState(structured.right.column);

  const leftCols = tables.find((t) => t.key === leftKey)?.columns ?? [];
  const rightCols = tables.find((t) => t.key === rightKey)?.columns ?? [];

  const sideSql = (parsed: ParsedOnSide, key: string, column: string): string => {
    if (key === parsed.table.key && column === parsed.column) return parsed.text;
    const table = tables.find((t) => t.key === key);
    return table ? formatOnSide(table, column, engine) : parsed.text;
  };

  return (
    <form
      className="flex flex-wrap items-center gap-1.5 rounded border border-input bg-background px-1.5 py-1"
      data-testid={`${testId}-form`}
      onSubmit={(e) => {
        e.preventDefault();
        onApply(`${sideSql(structured.left, leftKey, leftCol)} = ${sideSql(structured.right, rightKey, rightCol)}`);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          onCancel();
        }
      }}
    >
      <OnSide
        testId={`${testId}-on-left`}
        tables={tables}
        tableKey={leftKey}
        col={leftCol}
        columns={leftCols}
        onTableKey={(k) => {
          setLeftKey(k);
          const first = tables.find((t) => t.key === k)?.columns[0];
          if (first) setLeftCol(first.qualified.name);
        }}
        onCol={setLeftCol}
      />
      <span className="font-mono text-[11px] text-foreground">=</span>
      <OnSide
        testId={`${testId}-on-right`}
        tables={tables}
        tableKey={rightKey}
        col={rightCol}
        columns={rightCols}
        onTableKey={(k) => {
          setRightKey(k);
          const first = tables.find((t) => t.key === k)?.columns[0];
          if (first) setRightCol(first.qualified.name);
        }}
        onCol={setRightCol}
      />
      <Button type="submit" size="sm" className="h-6 px-2 text-[11px] active:scale-[0.96]" data-testid={`${testId}-apply`}>
        Apply
      </Button>
      <button
        type="button"
        className="text-[10px] text-muted-foreground hover:underline"
        data-testid={`${testId}-as-text`}
        onClick={onEditAsText}
      >
        Edit as text
      </button>
    </form>
  );
}

interface OnSideProps {
  readonly testId: string;
  readonly tables: readonly InScopeTable[];
  readonly tableKey: string;
  readonly col: string;
  readonly columns: readonly { qualified: { name: string } }[];
  readonly onTableKey: (key: string) => void;
  readonly onCol: (c: string) => void;
}

function OnSide({ testId, tables, tableKey, col, columns, onTableKey, onCol }: OnSideProps) {
  return (
    <span className="flex min-w-0 items-center gap-1 font-mono text-[11px]">
      <select
        data-testid={`${testId}-table`}
        className="min-w-0 max-w-[8rem] truncate rounded border border-input bg-background px-1 py-0.5"
        value={tableKey}
        onChange={(e) => onTableKey(e.target.value)}
      >
        {tables.map((t) => (
          <option key={t.key} value={t.key}>
            {t.label}
          </option>
        ))}
      </select>
      <span className="text-muted-foreground">.</span>
      <select
        data-testid={`${testId}-col`}
        className="min-w-0 max-w-[8rem] truncate rounded border border-input bg-background px-1 py-0.5"
        value={col}
        onChange={(e) => onCol(e.target.value)}
      >
        {columns.map((c, i) => (
          <option key={`${c.qualified.name}::${i}`} value={c.qualified.name}>
            {c.qualified.name}
          </option>
        ))}
      </select>
    </span>
  );
}
